import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { patientAPI, visitationAPI, medicineAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 900,
  margin: '0 auto',
  padding: theme.spacing(2),
}));

const PatientInfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.grey[200]}`,
}));

const MedicineItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[50],
}));

const TreatmentItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[50],
}));

function VisitationForm() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState({});
  const [medicineSuggestions, setMedicineSuggestions] = useState([]);

  const [formData, setFormData] = useState({
    doctorName: user?.name || '',
    visitDate: dayjs(),
    chiefComplaint: '',
    diagnosis: '',
    prescription: [
      {
        medicineName: '',
        dosage: '',
        frequency: 'BD',
        duration: '',
        instructions: ''
      }
    ],
    treatment: [
      {
        treatmentName: '',
        description: '',
        frequency: 'Monthly',
        sessions: ''
      }
    ],
    notes: '',
    followUpDate: null
  });

  useEffect(() => {
    fetchPatient();
    fetchMedicines();
  }, [patientId]);

  // Prefill doctor name when user data is available
  useEffect(() => {
    if (user?.name && !formData.doctorName) {
      setFormData(prev => ({ ...prev, doctorName: user.name }));
    }
  }, [user, formData.doctorName]);

  const fetchPatient = async () => {
    try {
      const response = await patientAPI.getById(patientId);
      setPatient(response.data.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch patient details');
      setLoading(false);
    }
  };

  const fetchMedicines = async () => {
    try {
      const response = await medicineAPI.getAll({ limit: 1000 });
      setMedicines(response.data.data);
    } catch (err) {
      console.error('Failed to fetch medicines');
    }
  };

  const handleMedicineSearch = async (searchTerm, index) => {
    if (searchTerm.length < 2) {
      setMedicineSuggestions([]);
      setShowSuggestions({ ...showSuggestions, [index]: false });
      return;
    }

    const filtered = medicines.filter(med =>
      med.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setMedicineSuggestions(filtered);
    setShowSuggestions({ ...showSuggestions, [index]: true });
  };

  const selectMedicine = (medicine, index) => {
    const updatedPrescription = [...formData.prescription];
    updatedPrescription[index] = {
      ...updatedPrescription[index],
      medicineName: medicine.name,
      dosage: medicine.defaultDosage || updatedPrescription[index].dosage,
      frequency: medicine.commonFrequencies?.[0] || updatedPrescription[index].frequency
    };

    setFormData({ ...formData, prescription: updatedPrescription });
    setShowSuggestions({ ...showSuggestions, [index]: false });
  };

  const handlePrescriptionChange = (index, field, value) => {
    const updatedPrescription = [...formData.prescription];
    updatedPrescription[index][field] = value;
    setFormData({ ...formData, prescription: updatedPrescription });

    if (field === 'medicineName') {
      handleMedicineSearch(value, index);
    }
  };

  const addPrescription = () => {
    setFormData({
      ...formData,
      prescription: [
        ...formData.prescription,
        {
          medicineName: '',
          dosage: '',
          frequency: 'BD',
          duration: '',
          instructions: ''
        }
      ]
    });
  };

  const removePrescription = (index) => {
    const updatedPrescription = formData.prescription.filter((_, i) => i !== index);
    setFormData({ ...formData, prescription: updatedPrescription });
  };

  const handleTreatmentChange = (index, field, value) => {
    const updatedTreatment = [...formData.treatment];
    updatedTreatment[index][field] = value;
    setFormData({ ...formData, treatment: updatedTreatment });
  };

  const addTreatment = () => {
    setFormData({
      ...formData,
      treatment: [
        ...formData.treatment,
        {
          treatmentName: '',
          description: '',
          frequency: 'Monthly',
          sessions: ''
        }
      ]
    });
  };

  const removeTreatment = (index) => {
    const updatedTreatment = formData.treatment.filter((_, i) => i !== index);
    setFormData({ ...formData, treatment: updatedTreatment });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Filter out empty prescriptions and treatments
      const filteredPrescription = formData.prescription.filter(
        p => p.medicineName && p.medicineName.trim() !== ''
      );
      const filteredTreatment = formData.treatment.filter(
        t => t.treatmentName && t.treatmentName.trim() !== ''
      );

      const visitationData = {
        patientId: parseInt(patientId),
        ...formData,
        prescription: filteredPrescription,
        treatment: filteredTreatment,
        visitDate: formData.visitDate?.toISOString(),
        followUpDate: formData.followUpDate?.toISOString() || null
      };

      await visitationAPI.create(visitationData);
      setSuccess('Visitation saved successfully!');

      setTimeout(() => {
        navigate(`/patient/${patientId}`);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save visitation');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  if (loading && !patient) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!patient) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Patient not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <StyledCard>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
            🏥 New Visitation
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <PatientInfoCard>
            <Typography variant="h6" gutterBottom>
              👤 Patient Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Name:</strong> {patient.firstName} {patient.lastName || ''}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Age/Gender:</strong> {calculateAge(patient.dateOfBirth)} years / {patient.gender}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Phone:</strong> {patient.phone}</Typography>
              </Grid>
              {patient.allergies && (
                <Grid item xs={12}>
                  <Chip
                    label={`⚠️ Allergies: ${patient.allergies}`}
                    color="error"
                    variant="outlined"
                    sx={{ fontWeight: 'bold', mt: 1 }}
                  />
                </Grid>
              )}
              {patient.medicalHistory && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    🏥 Medical History
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                    {patient.medicalHistory}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </PatientInfoCard>

          <Box component="form" onSubmit={handleSubmit}>
            <SectionCard>
              <Typography variant="h6" gutterBottom>
                📝 Visit Details
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6} size={6}>
                  <TextField
                    fullWidth
                    label="Doctor Name *"
                    value={formData.doctorName}
                    onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Visit Date & Time *"
                    value={formData.visitDate}
                    onChange={(newValue) => setFormData({ ...formData, visitDate: newValue })}
                    sx={{ width: '100%' }}
                  />
                </Grid>

                <Grid item xs={12} size={6}>
                  <TextField
                    fullWidth
                    label="Chief Complaint"
                    value={formData.chiefComplaint}
                    onChange={(e) => setFormData({ ...formData, chiefComplaint: e.target.value })}
                    multiline
                    rows={2}
                    placeholder="Patient's main complaint or reason for visit..."
                  />
                </Grid>

                <Grid item xs={12} size={6}>
                  <TextField
                    fullWidth
                    label="Diagnosis"
                    value={formData.diagnosis}
                    onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                    multiline
                    rows={2}
                    placeholder="Enter diagnosis..."
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  💊 Prescription
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addPrescription}
                  size="small"
                >
                  Add Medicine
                </Button>
              </Box>

              {formData.prescription.map((prescription, index) => (
                <MedicineItem key={index}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2} size={5}>
                      <Autocomplete
                        freeSolo
                        options={medicines.map(med => med.name)}
                        value={prescription.medicineName}
                        onChange={(event, newValue) => {
                          handlePrescriptionChange(index, 'medicineName', newValue || '');
                          if (newValue) {
                            const selectedMedicine = medicines.find(med => med.name === newValue);
                            if (selectedMedicine) {
                              selectMedicine(selectedMedicine, index);
                            }
                          }
                        }}
                        onInputChange={(event, newInputValue) => {
                          handlePrescriptionChange(index, 'medicineName', newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Medicine Name"
                            size="small"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Dosage"
                        value={prescription.dosage}
                        onChange={(e) => handlePrescriptionChange(index, 'dosage', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Frequency</InputLabel>
                        <Select
                          value={prescription.frequency}
                          label="Frequency"
                          onChange={(e) => handlePrescriptionChange(index, 'frequency', e.target.value)}
                        >
                          <MenuItem value="OD">OD (Once Daily)</MenuItem>
                          <MenuItem value="BD">BD (Twice Daily)</MenuItem>
                          <MenuItem value="TDS">TDS (Thrice Daily)</MenuItem>
                          <MenuItem value="QID">QID (Four Times Daily)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Duration"
                        value={prescription.duration}
                        onChange={(e) => handlePrescriptionChange(index, 'duration', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={2} size={8}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Instructions"
                        multiline
                        rows={2}
                        value={prescription.instructions}
                        onChange={(e) => handlePrescriptionChange(index, 'instructions', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={1}>
                      <IconButton
                        color="error"
                        onClick={() => removePrescription(index)}
                        size="small"
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </MedicineItem>
              ))}
            </SectionCard>

            <SectionCard>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  🩺 Treatment
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addTreatment}
                  size="small"
                >
                  Add Treatment
                </Button>
              </Box>

              {formData.treatment.map((treatment, index) => (
                <TreatmentItem key={index}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3} size={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Treatment Name"
                        value={treatment.treatmentName}
                        onChange={(e) => handleTreatmentChange(index, 'treatmentName', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Frequency</InputLabel>
                        <Select
                          value={treatment.frequency}
                          label="Frequency"
                          onChange={(e) => handleTreatmentChange(index, 'frequency', e.target.value)}
                        >
                          <MenuItem value="Once">Once</MenuItem>
                          <MenuItem value="Daily">Daily</MenuItem>
                          <MenuItem value="Twice Daily">Twice Daily</MenuItem>
                          <MenuItem value="Weekly">Weekly</MenuItem>
                          <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                          <MenuItem value="Every 3 weeks">Every 3 weeks</MenuItem>
                          <MenuItem value="Every 4 weeks">Every 4 weeks</MenuItem>
                          <MenuItem value="Monthly">Monthly</MenuItem>
                          <MenuItem value="Every 45 Days">Every 45 Days</MenuItem>
                          <MenuItem value="Quarterly">Quarterly</MenuItem>
                          <MenuItem value="Half-Yearly">Half-Yearly</MenuItem>
                          <MenuItem value="Yearly">Yearly</MenuItem>
                          <MenuItem value="As Needed">As Needed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="No. of Sessions"
                        value={treatment.sessions}
                        onChange={(e) => handleTreatmentChange(index, 'sessions', e.target.value)}
                      />
                    </Grid>


                    <Grid item xs={12} md={3} size={10}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        multiline
                        rows={2}
                        value={treatment.description}
                        onChange={(e) => handleTreatmentChange(index, 'description', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <IconButton
                        color="error"
                        onClick={() => removeTreatment(index)}
                        size="small"
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </TreatmentItem>
              ))}
            </SectionCard>

            <SectionCard>
              <Typography variant="h6" gutterBottom>
                📄 Additional Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6} size={12}>
                  <TextField
                    fullWidth
                    label="Instructions"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    multiline
                    rows={2}
                    placeholder="Enter any instructions..."
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Follow-up Date (Optional)"
                    value={formData.followUpDate}
                    onChange={(newValue) => setFormData({ ...formData, followUpDate: newValue })}
                    sx={{ width: '100%' }}
                    slotProps={{
                      textField: {
                        helperText: 'Schedule a follow-up visit if needed'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/patient/${patientId}`)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading && <CircularProgress size={16} />}
              >
                {loading ? 'Saving...' : 'Save Visitation'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
}

export default VisitationForm;
